import {gql} from '@apollo/client';
import {compact} from '@benchling/js-utils';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {ComplexPolymerContentsQuery} from 'modbio/complex-polymers/__generated/ComplexPolymerContentsQuery';

export type ComplexPolymerGraphQL = ExtractByTypename<
  ComplexPolymerContentsQuery['complexPolymer'],
  'ComplexPolymerNode'
>;
export type ComplexPolymerVersionGraphQL = ExtractByTypename<
  ComplexPolymerGraphQL['version'],
  'ComplexPolymerVersionNode'
>;

export type OligoConjugateGraphQL = ExtractByTypename<
  ComplexPolymerContentsQuery['complexPolymer'],
  'OligoConjugateNode'
>;
export type OligoConjugateVersionGraphQL = ExtractByTypename<
  OligoConjugateGraphQL['version'],
  'OligoConjugateVersionNode'
>;
export type OligoConjugateOligoGraphQL = NonNullable<OligoConjugateVersionGraphQL['oligo']>;
export type OligoConjugateConjugateGraphQL = OligoConjugateVersionGraphQL['conjugates'][number];
export type OligoConjugateResolvedAttachmentGraphQL =
  OligoConjugateVersionGraphQL['resolvedAttachments'][number];
export type OligoConjugateBranchedAttachmentGraphQL =
  OligoConjugateVersionGraphQL['branchedAttachments'][number];

export type OligoDuplexGraphQL = ExtractByTypename<
  ComplexPolymerContentsQuery['complexPolymer'],
  'OligoDuplexNode'
>;
export type OligoDuplexVersionGraphQL = ExtractByTypename<
  OligoDuplexGraphQL['version'],
  'OligoDuplexVersionNode'
>;
export type OligoDuplexOligoStrandGraphQL =
  | NonNullable<OligoDuplexVersionGraphQL['forwardStrand']>
  | NonNullable<OligoDuplexVersionGraphQL['reverseStrand']>;
export type OligoDuplexOligoGraphQL = OligoDuplexOligoStrandGraphQL['oligo'];
export type OligoDuplexConjugateGraphQL = OligoDuplexOligoStrandGraphQL['conjugates'][number];
export type OligoDuplexResolvedAttachmentGraphQL =
  OligoDuplexOligoStrandGraphQL['resolvedAttachments'][number];
export type OligoDuplexBranchedAttachmentGraphQL =
  OligoDuplexOligoStrandGraphQL['branchedAttachments'][number];

export type ConjugateGraphQL = OligoConjugateConjugateGraphQL | OligoDuplexConjugateGraphQL;
export type ResolvedAttachmentGraphQL =
  | OligoConjugateResolvedAttachmentGraphQL
  | OligoDuplexResolvedAttachmentGraphQL;
export type LinkedMoleculeGraphQL = ConjugateGraphQL['linkedMolecules'][number];
export type OligoGraphQL = OligoConjugateOligoGraphQL | OligoDuplexOligoGraphQL;
export type BranchedAttachmentGraphQL =
  | OligoConjugateBranchedAttachmentGraphQL
  | OligoDuplexBranchedAttachmentGraphQL;

export type ConjugateConnectionGraphQL = ConjugateGraphQL['connection'];
export type LinkedMoleculeConnectionGraphQL = ConjugateGraphQL['linkedMolecules'][number]['connection'];
export type ConjugateConnectionSideGraphQL =
  | ConjugateGraphQL['connection']['source']
  | ConjugateGraphQL['connection']['target'];
export type SourceLinkedMoleculeConnectionSideGraphQL =
  ConjugateGraphQL['linkedMolecules'][number]['connection']['source'];
export type SourceLinkedMoleculeConnectionSideEntityGraphQL =
  SourceLinkedMoleculeConnectionSideGraphQL['referencedPolymer']['entity'];

export type ConjugateEntityGraphQL = ConjugateGraphQL['entity'];
export type LinkedMoleculeEntityGraphQL = ConjugateGraphQL['linkedMolecules'][number]['entity'];

export type SimplePolymerGraphQL =
  | NonNullable<OligoConjugateVersionGraphQL['structure']>['polymers'][number]
  | NonNullable<OligoDuplexVersionGraphQL['structure']>['polymers'][number];
export type MoleculeSimplePolymerGraphQL = ExtractByTypename<SimplePolymerGraphQL, 'MoleculePolymerNode'>;
export type OligoSimplePolymerGraphQL = ExtractByTypename<
  SimplePolymerGraphQL,
  'NucleotideSequencePolymerNode'
>;
export type SequenceEntityGraphQL = ExtractByTypename<OligoSimplePolymerGraphQL['entity'], 'SequenceNode'>;

export type ConnectionGraphQL =
  | NonNullable<OligoConjugateVersionGraphQL['structure']>['connections'][number]
  | NonNullable<OligoDuplexVersionGraphQL['structure']>['connections'][number];
export type ConnectionSideGraphQL = ConnectionGraphQL['source'] | ConnectionGraphQL['target'];

export type StructureGraphGraphQL =
  | NonNullable<OligoConjugateVersionGraphQL['structureGraph']>
  | NonNullable<OligoDuplexVersionGraphQL['structureGraph']>;
export type StructureGraphNodeGraphQL = StructureGraphGraphQL['nodes'][number];
export type StructureGraphEdgeGraphQL = StructureGraphGraphQL['edges'][number];
export type StructureGraphNodeTypeGraphQL = StructureGraphNodeGraphQL['type'];
export type NucleotideStructureGraphNodeDataGraphQL = ExtractByTypename<
  StructureGraphNodeGraphQL['data'],
  'NucleotideStructureGraphNodeDataNode'
>;
export type ConjugateStructureGraphNodeDataGraphQL = ExtractByTypename<
  StructureGraphNodeGraphQL['data'],
  'ConjugateStructureGraphNodeDataNode'
>;

// Keep in sync with benchling/modbio/complex_polymers/core/complex_polymer_type.py
// All types including types under development and not released yet.
// TODO(BNCH-122853): This is not currently in sync with the BE version because we have not gotten around to removing Antibody from this enum on the backend
export enum ComplexPolymerType {
  OLIGO_CONJUGATE = 'OLIGO_CONJUGATE',
  OLIGO_DUPLEX = 'OLIGO_DUPLEX',
}

// Types that will be visible in the UI and available to customers.
export const getSupportedPolymerTypes = (): Array<ComplexPolymerType> =>
  compact([ComplexPolymerType.OLIGO_CONJUGATE, ComplexPolymerType.OLIGO_DUPLEX]);

export const COMPLEX_POLYMER_DISPLAY_NAMES: {[index in ComplexPolymerType]: string} = {
  [ComplexPolymerType.OLIGO_CONJUGATE]: 'oligo conjugate',
  [ComplexPolymerType.OLIGO_DUPLEX]: 'oligo duplex',
};

export const ANTIBODY_DISPLAY_NAME = 'antibody';

export const COMPLEX_POLYMER_TYPE_TO_DESCRIPTION: {[key in ComplexPolymerType]: string} = {
  [ComplexPolymerType.OLIGO_CONJUGATE]: 'Single stranded oligo with one or more conjugated molecules',
  [ComplexPolymerType.OLIGO_DUPLEX]: 'Double stranded oligo duplex with one or more conjugated molecules',
};

// Map of residue index on oligo to all conjugates attached at that position
// Conjugate consists of molecule directly attached to oligo, as well as list of molecules connected to that molecule
export interface ConjugatesMap {
  [accessor: number]: Array<ConjugateGraphQL>;
}

export interface EntityAssociations {
  [apiId: string]: Array<string>;
}

// Keep in sync with HELMPolymerType in benchling/molbio/helm/constants.py.
export enum HELMPolymerType {
  RNA = 'RNA',
  PEPTIDE = 'PEPTIDE',
  CHEM = 'CHEM',
  PROTEIN = 'PROTEIN',
}

export interface IsContainedInComplexPolymerData {
  isContained: boolean;
  errorMessage: string | null;
}

export const MODEL_TYPE_TO_HELM_POLYMER_TYPE: Partial<Record<ModelTypeConstants, HELMPolymerType>> = {
  [ModelTypeConstants.SEQUENCE]: HELMPolymerType.RNA,
  [ModelTypeConstants.PROTEIN]: HELMPolymerType.PEPTIDE,
  [ModelTypeConstants.MOLECULE]: HELMPolymerType.CHEM,
};

// Keep in sync with ComplexPolymerBondConnectionSide and ComplexPolymerConnection in
// `benchling/modbio/complex_polymers/core/complex_polymer_structure.py`. Noting that we are using snake case here so
// that it's compatible with the backend, and this will only be populated when we're preparing the data to be
// sent to the backend.
export enum NucleotideModificationType {
  BASE = 'BASE',
  PHOSPHATE = 'PHOSPHATE',
  SUGAR = 'SUGAR',
}

export enum ComplexPolymerConnectionType {
  COVALENT_BOND = 'COVALENT_BOND',
  HYDROGEN_BOND = 'HYDROGEN_BOND',
}

// Keep in sync with benchling/modbio/complex_polymers/domain/constants.py:ComplexPolymerAttachmentStyle.
export enum ComplexPolymerAttachmentStyle {
  LEAVING_GROUP = 'LEAVING_GROUP',
  DRAWN = 'DRAWN',
}

export interface ComplexPolymerBondConnectionSide {
  polymer_ref: string;
  residue_index: number | null;
  modification_type: NucleotideModificationType | null;
  r_group: number | null;
}

export interface ComplexPolymerConnection {
  connection_type: ComplexPolymerConnectionType;
  attachment_style: ComplexPolymerAttachmentStyle | null;
  source: ComplexPolymerBondConnectionSide;
  target: ComplexPolymerBondConnectionSide;
}

// Resolved attachment input format expected by backend endpoints
export interface ResolvedAttachmentInput {
  molfile: string;
  connections_involved: Array<ComplexPolymerConnection>;
}

function connectionSideHash(connectionSide: ComplexPolymerBondConnectionSide): string {
  return `${connectionSide.polymer_ref}-${connectionSide.residue_index}-${connectionSide.modification_type}-${connectionSide.r_group}`;
}

export function connectionHash(connection: ComplexPolymerConnection): string {
  const connectionSideHashA = connectionSideHash(connection.source);
  const connectionSideHashB = connectionSideHash(connection.target);
  if (connectionSideHashA < connectionSideHashB) {
    return `${connection.connection_type}-${connection.attachment_style}-${connectionSideHashA}-${connectionSideHashB}`;
  }
  return `${connection.connection_type}-${connection.attachment_style}-${connectionSideHashB}-${connectionSideHashA}`;
}

export function resolvedAttachmentHash(resolvedAttachment: ResolvedAttachmentInput): string {
  return `${resolvedAttachment.molfile}-${resolvedAttachment.connections_involved.map((connection) => connectionHash(connection)).join(',')}`;
}

export const COMPLEX_POLYMER_DISPLAY_TEXT = 'complex polymer';

// Keep in sync with benchling/modbio/complex_polymers/core/complex_polymer_type.py
export const OLIGO_CONJUGATE_BACKBONE_STRAND_REF = 'RNA1';

export const OLIGO_DUPLEX_FORWARD_STRAND_REF = 'RNA1';
export const OLIGO_DUPLEX_REVERSE_STRAND_REF = 'RNA2';

// Keep in sync with StrandType in benchling/modbio/complex_polymers/core/complex_polymer_type.py
export enum StrandType {
  FORWARD = 'FORWARD',
  REVERSE = 'REVERSE',
}

export const STRAND_TYPE_TO_DISPLAY_NAME: {[key in StrandType]: string} = {
  [StrandType.FORWARD]: 'Forward',
  [StrandType.REVERSE]: 'Reverse',
};

export interface ComplexPolymerInput {
  entityAssociations: EntityAssociations;
  connectionInputs: Array<ComplexPolymerConnection>;
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>;
  offset: number | null;
}

// Fake sequence ID that is used for the modified nucleotides legend. The legend uses the sequence ID to query
// for the sequence's structural data; however, our usage passes in the structural data to this component
// so the query is not run and the sequence ID is not used
export const COMPLEX_POLYMER_FAKE_SEQUENCE_ID = 'seq_fake';

export const COMPLEX_POLYMER_CONTENTS_QUERY = gql`
  query ComplexPolymerContentsQuery($complexPolymerId: StrictString!, $versionId: StrictString!)
  @engComponent(name: MODIFIED_BIOLOGICS)
  @skipAudit {
    complexPolymer(id: $complexPolymerId) {
      ... on ComplexPolymerNode {
        id
      }
      ... on OligoConjugateNode {
        id
        name
        version(versionId: $versionId) {
          ... on OligoConjugateVersionNode {
            id
            oligo {
              id
              helmReference
              bases
              entity {
                ... on RegistryItemInterface {
                  id
                  name
                }
                ...InaccessibleInfoGraphQL
              }
              modifiedNucleotides {
                position
                sugarMonomerId
                baseMonomerId
                phosphateMonomerId
              }
              nucleotideType
            }
            conjugates {
              id
              polymerHelmReference
              residueIndex
              entity {
                ... on RegistryItemInterface {
                  id
                  name
                }
                ...InaccessibleInfoGraphQL
              }
              connection {
                connectionType
                attachmentStyle
                source {
                  polymerHelmReference
                  residueIndex
                  modificationType
                  monomerName
                  rGroup
                }
                target {
                  polymerHelmReference
                  residueIndex
                  modificationType
                  monomerName
                  rGroup
                }
              }
              linkedMolecules {
                id
                polymerHelmReference
                entity {
                  ... on RegistryItemInterface {
                    id
                    name
                  }
                  ...InaccessibleInfoGraphQL
                }
                connection {
                  connectionType
                  attachmentStyle
                  source {
                    polymerHelmReference
                    residueIndex
                    modificationType
                    monomerName
                    rGroup
                    referencedPolymer {
                      ... on SimplePolymerInterface {
                        id
                        helmReference
                        entity {
                          ... on RegistryItemInterface {
                            id
                            name
                          }
                          ...InaccessibleInfoGraphQL
                        }
                      }
                    }
                  }
                  target {
                    polymerHelmReference
                    residueIndex
                    modificationType
                    monomerName
                    rGroup
                  }
                }
              }
            }
            resolvedAttachments {
              ...ResolvedAttachmentGraphQL
            }
            branchedAttachments {
              id
              displayName
              residueIndex
            }
          }
        }
        currentVersion {
          id
        }
      }
      ... on OligoDuplexNode {
        id
        name
        version(versionId: $versionId) {
          ... on OligoDuplexVersionNode {
            id
            forwardStrand {
              ... on OligoStrandNode {
                ...OligoStrandGraphQL
              }
            }
            reverseStrand {
              ... on OligoStrandNode {
                ...OligoStrandGraphQL
              }
            }
            offset
          }
        }
        currentVersion {
          id
        }
      }
      ... on ComplexPolymerInterface {
        version(versionId: $versionId) {
          ... on ComplexPolymerVersionInterface {
            id
            structure {
              polymers {
                ... on SimplePolymerInterface {
                  id
                  polymerType
                  helmReference
                  entity {
                    ... on RegistryItemInterface {
                      id
                      name
                      archiveRecord {
                        ...ArchiveRecordGraphQL
                      }
                    }
                    ... on SequenceNode {
                      id
                      iconId
                      bases
                      modifiedNucleotides {
                        position
                        sugarMonomerId
                        baseMonomerId
                        phosphateMonomerId
                      }
                      nucleotideType
                    }
                    ...InaccessibleInfoGraphQL
                  }
                }
                ... on MoleculePolymerNode {
                  id
                  chemicalStructureImage {
                    ... on SVGNode {
                      base64Svg
                    }
                    ... on ErrorNode {
                      message
                    }
                  }
                }
              }
              connections {
                ...ComplexPolymerConnectionGraphQL
              }
            }
            structureGraph {
              nodes {
                id
                type
                position {
                  x
                  y
                }
                draggable
                selectable
                data {
                  ... on StructureGraphNodeDataInterface {
                    entityId
                    polymerRef
                  }
                  ... on NucleotideStructureGraphNodeDataNode {
                    residueIndex
                    base
                    modifiedNucleotide {
                      position
                      sugarMonomerId
                      baseMonomerId
                      phosphateMonomerId
                    }
                    nucleotideType
                  }
                }
              }
              edges {
                id
                sourceId
                sourceHandle
                targetId
                targetHandle
              }
            }
            outOfSync
            outOfSyncReason
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ComplexPolymerConnection').fragment}
  ${getFragmentWrapper('ResolvedAttachment').fragment}
  ${getFragmentWrapper('OligoStrand').fragment}
  ${getFragmentWrapper('ArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;
